import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import {
  useCustomSearchParams,
  useTrackedCustomSearchParams,
} from 'common/hooks/customSearchParams';
import _ from 'lodash';
import { getCountForKey } from 'pages/TrackAndTrace/components/controls/filters/MoreFilters';
import { WatchListFilters } from 'services/clients/watchList';
import { SegmentEvent } from 'services/helpers/segment';
import { SHIPMENTS_QUERY_PARAMS } from 'pages/Shipments/params';
import * as React from 'react';
import {
  DEFAULT_SHIPMENT_COLUMNS,
  GetColumns,
  generateDefaultActiveColumnVisibilityModel,
} from './Table/columns';
import { PresetView, PRESET_DEFINITIONS, UnwatchedState } from './types';
import { OnCellClick } from './Table/cells/types';

const rootFilterKeys = [
  'billOfLadingNumbers',
  'customers',
  'portPickupCodes',
  'status',
  'terminalPickupKeys',
];

const nestedFilterKeys = [
  'availableForPickup',
  'closedArea',
  'containerStatus',
  'containerType',
  'demurrageDue',
  'gatedInAt',
  'gatedOutAt',
  'hasPerDiemFees',
  'holdTypes',
  'lastFreeDayAt',
  'portPickupCodes',
  'shippingLine',
  'terminalPickupKeys',
  'estPerDiemLfd',
];

const allKeys = [...rootFilterKeys, ...nestedFilterKeys];

/**
 * Clear all the filters
 */
export const useClearFilters = (all = false) => {
  const { searchParams, setSearchParams } = useCustomSearchParams(SHIPMENTS_QUERY_PARAMS);

  return React.useCallback(() => {
    const resetFilters: typeof searchParams = {};
    (all ? allKeys : nestedFilterKeys).forEach((k) => {
      resetFilters[k] = null;
    });
    setSearchParams(resetFilters);
  }, [setSearchParams, all]);
};

/**
 * @returns a boolean whether the user has enabled any filters
 */
export const useHasUserFilters = (all = false) => {
  const { searchParams } = useCustomSearchParams(SHIPMENTS_QUERY_PARAMS);

  const count = React.useMemo(() => {
    let total = 0;
    (all ? allKeys : nestedFilterKeys).forEach((k) => {
      total += getCountForKey(searchParams, k);
    });
    return total;
  }, [searchParams, all]);

  return count > 0;
};

const parseColumnVisibilityModel: (
  key: string,
  presetView: PresetView
) => GridColumnVisibilityModel = (key, presetView) => {
  let columnsModel = generateDefaultActiveColumnVisibilityModel(presetView);
  const raw = localStorage.getItem(key);
  if (raw) {
    // Combine the local storage model with the generated model so that
    // we don't lose any columns that may have been added in the future
    columnsModel = { ...columnsModel, ...JSON.parse(raw) };
  }
  return columnsModel;
};

/**
 * Provides a getter and setter for the column visibility model that
 * also saves the model to local storage so that it persists between
 * application loads.
 * @param presetView the preset view that we want the column visibility for
 */
export const useColumnVisibility = (
  presetView: PresetView
): [GridColumnVisibilityModel, (model: GridColumnVisibilityModel) => void] => {
  const key = React.useMemo(() => `shippers_watchlist_${presetView}_columns`, [presetView]);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    React.useState<GridColumnVisibilityModel>(parseColumnVisibilityModel(key, presetView));

  const setColumnVisibility = React.useCallback(
    (model: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(model);
      localStorage.setItem(key, JSON.stringify(model));
    },
    [setColumnVisibilityModel, key]
  );

  React.useEffect(() => {
    setColumnVisibilityModel(parseColumnVisibilityModel(key, presetView));
  }, [presetView, key]);

  return [columnVisibilityModel, setColumnVisibility];
};

/**
 * Combines the preset view's default filters with the current search params filters
 * to produce a watchlist filter object
 * @param presetView the current preset view
 * @returns a watchlist filter object
 */
export const useWatchlistFilters = (presetView?: PresetView): WatchListFilters => {
  const { searchParams } = useCustomSearchParams(SHIPMENTS_QUERY_PARAMS);
  const filters: WatchListFilters = React.useMemo(() => {
    const { focusedContainer, ...filterParams } = searchParams;
    return {
      ...(presetView ? PRESET_DEFINITIONS[presetView].filters : {}),
      ...filterParams,
      onlyShipments: true,
    };
  }, [searchParams, presetView]);

  return filters;
};

export const useDeletedWatchlistFilters = (): WatchListFilters => {
  const { searchParams } = useCustomSearchParams(SHIPMENTS_QUERY_PARAMS);
  const filters: WatchListFilters = React.useMemo(() => {
    const { focusedContainer, ...filterParams } = searchParams;
    return {
      ...filterParams,
      onlyShipments: true,
      deleted: true,
      unwatchedState: UnwatchedState.WatchedAndUnwatched,
    };
  }, [searchParams]);

  return filters;
};

export const useFilterSearchParams = (resetPageParamOnUpdate = false) => {
  return useTrackedCustomSearchParams(
    SegmentEvent.ShipmentsWatchlistFilterUsed,
    SHIPMENTS_QUERY_PARAMS,
    resetPageParamOnUpdate
  );
};

/** Returns the current preset view based on the filters; defaults to active if no match found */
export const usePresetView = () => {
  const { searchParams } = useCustomSearchParams(SHIPMENTS_QUERY_PARAMS);
  return React.useMemo(() => {
    const foundPreset = Object.values(PRESET_DEFINITIONS)
      .filter((p) => p.presetView !== PresetView.Active)
      .find((preset) => {
        const { filters } = preset;

        const subset = _.pick(searchParams, Object.keys(filters));
        return _.isEqual(subset, filters);
      });

    return foundPreset?.presetView || PresetView.Active;
  }, [searchParams]);
};

/** Translate column visibility objects to a list of activated columns */
export const translateVisibility = (columnVisibilityModel: GridColumnVisibilityModel) => {
  const output: string[] = [...DEFAULT_SHIPMENT_COLUMNS.map((c) => _.snakeCase(c))];
  Object.keys(columnVisibilityModel).forEach((key) => {
    if (columnVisibilityModel[key]) {
      output.push(_.snakeCase(key));
    }
  });
  return output;
};

export const useTableColumns = (columnFunction: GetColumns, onCellClick?: OnCellClick) => {
  const { searchParams, setSearchParams } = useCustomSearchParams(SHIPMENTS_QUERY_PARAMS);

  const [showTooltip, setShowTooltip] = React.useState('');
  const { sortBy, sortDirection } = searchParams;

  const setSortSearchParams = React.useCallback(
    (field: string) => {
      if (sortDirection === null || field !== sortBy) {
        // If we're not sorting yet, or we're changing the field we're sorting by,
        // we want to sort ascending on first click
        setSearchParams({ sortBy: field, sortDirection: 'asc' });
      } else if (sortDirection === 'asc') {
        setSearchParams({ sortBy: field, sortDirection: 'desc' });
      } else if (sortDirection === 'desc') {
        setSearchParams({ sortBy: null, sortDirection: null });
      }
    },
    [sortBy, sortDirection, setSearchParams]
  );

  const columns = React.useMemo(
    () => columnFunction(showTooltip, setSortSearchParams, sortBy, sortDirection, onCellClick),
    [showTooltip, setSortSearchParams, sortBy, sortDirection, columnFunction, onCellClick]
  );

  return { columns, setShowTooltip, showTooltip: !!showTooltip };
};
